/**
 * batch-69 tvt-saldi (FO-513 §C7) - één bron voor de TVT-opbouwfactor per week
 * (overuren) en per dag (reistijd), voor opbouw en verloning.
 *
 * Tot batch-69 rekende elk pad de factor zelf uit `tvtFactor` met een eigen
 * datum: de aggregator op de maandag van de week (werk) en de dag (reis), de
 * handmatige regelwijziging op de laatste dag van de periode voor de hele regel,
 * en het correctiepad op de startdatum van de werkbon. Bij een keuzewissel midden
 * in een periode liepen tijd-kant (TVT) en geld-kant (loon) daardoor uiteen:
 * dezelfde uren werden opgebouwd én volledig uitbetaald. Alle paden gebruiken nu
 * deze functies; de drift-test `tvt-factor-een-bron-drift.test.ts` bewaakt dat.
 *
 *   - Overuren: de instelling op de **maandag** van de ISO-week (het week-net
 *     overwerk is ondeelbaar per week, zie `triggerTVTFromWeekstaat`).
 *   - Reistijd: de instelling op de **dag** zelf.
 */
import { tvtFactor } from './payroll-calculations'
import { getIsoWeek, getWeekDateRange } from './uren-calculations'

/** De TVT-instellingen van één contractmutatie (of `null` = geen geldig contract). */
export interface TvtInstellingen {
  tvtWerkurenEnabled: boolean
  tvtReisurenEnabled: boolean
  atvEnabled: boolean
  /** 50 of 100; Decimal of number (de functie normaliseert). */
  tvtWerkOpbouwPercentage: number | { toString(): string } | null | undefined
  tvtReisOpbouwPercentage: number | { toString(): string } | null | undefined
}

/**
 * De ankerdatum voor de overuren-factor: de ISO-maandag (UTC-middernacht) van de
 * week waarin `datum` valt. Een correctie op woensdag rekent dus met de keuze van
 * maandag, net als de opbouw van die week.
 */
export function tvtWerkAnker(datum: Date): Date {
  const { year, week } = getIsoWeek(datum)
  return getWeekDateRange(year, week).start
}

/** Overuren: factor (0 / 0,5 / 1) uit de instelling op de maandag van de week. */
export function tvtWerkFactorVoorWeek(instellingOpMaandag: TvtInstellingen | null): number {
  if (!instellingOpMaandag) return 0
  return tvtFactor(
    instellingOpMaandag.tvtWerkurenEnabled,
    instellingOpMaandag.atvEnabled,
    Number(instellingOpMaandag.tvtWerkOpbouwPercentage ?? 100),
  )
}

/** Reistijd: factor (0 / 0,5 / 1) uit de instelling op de dag zelf. */
export function tvtReisFactorVoorDag(instellingOpDag: TvtInstellingen | null): number {
  if (!instellingOpDag) return 0
  return tvtFactor(
    instellingOpDag.tvtReisurenEnabled,
    instellingOpDag.atvEnabled,
    Number(instellingOpDag.tvtReisOpbouwPercentage ?? 100),
  )
}

/**
 * De geld-kant van één week overwerk bij factor `f`: het opgebouwde deel
 * (`uren × f`, per week op 2 decimalen, exact als de TVT-boeking) krijgt alleen
 * de 0,5x-premie, de rest (`uren - opbouw`) wordt vol (1,5x) uitbetaald.
 */
export function splitsOverwerkWeek(uren: number, factor: number): { opbouw: number; vol: number } {
  const r2 = (n: number) => Math.round(n * 100) / 100
  const opbouw = factor > 0 ? r2(uren * factor) : 0
  return { opbouw, vol: Math.max(0, r2(uren - opbouw)) }
}

/** Idem voor de reistijd van één dag: opgebouwd deel gaat naar TVT, de rest wordt uitbetaald. */
export function splitsReisDag(uren: number, factor: number): { opbouw: number; vol: number } {
  return splitsOverwerkWeek(uren, factor)
}

// ── Dubbel-indicator (FO-513 §B4, §C9) ──────────────────────────────────────

export type TvtDubbelStatus = 'OK' | 'DUBBEL' | 'TEKORT'

export interface TvtPeriodeBeoordeling {
  /** Wat volgens de huidige keuze naar TVT hoort (bruto × factor). */
  verwachtOpbouw: number
  /** Wat er vol is uitbetaald (uit de verloning, of verwacht volgens de keuze). */
  uitbetaaldVol: number
  /** Uren die zowel als TVT zijn opgebouwd als vol zijn uitbetaald. */
  dubbelUren: number
  /** Uren die nergens zijn gecompenseerd (niet opgebouwd en niet uitbetaald). */
  tekortUren: number
  status: TvtDubbelStatus
}

/**
 * Pure beoordeling van één week (overuren) of dag (reistijd):
 * `opgebouwd + uitbetaaldVol` hoort precies `bruto` te zijn. Meer = dubbel
 * gecompenseerd (rood), minder = niet gecompenseerd. Zonder bekende uitbetaling
 * (`uitbetaaldVol` null) rekent hij met wat de verloning bij de huidige keuze
 * betaalt (`bruto - bruto × factor`). Tolerantie 0,01 u (afronding per week).
 */
export function beoordeelTvtPeriode(input: {
  bruto: number
  factor: number
  opgebouwd: number
  uitbetaaldVol?: number | null
}): TvtPeriodeBeoordeling {
  const r2 = (n: number) => Math.round(n * 100) / 100
  const { opbouw: verwachtOpbouw, vol: verwachtVol } = splitsOverwerkWeek(Math.max(0, input.bruto), input.factor)
  const uitbetaaldVol = r2(input.uitbetaaldVol ?? verwachtVol)
  const som = r2(input.opgebouwd + uitbetaaldVol)
  const bruto = r2(Math.max(0, input.bruto))
  const dubbelUren = som - bruto > 0.01 ? r2(som - bruto) : 0
  const tekortUren = bruto - som > 0.01 ? r2(bruto - som) : 0
  return {
    verwachtOpbouw,
    uitbetaaldVol,
    dubbelUren,
    tekortUren,
    status: dubbelUren > 0 ? 'DUBBEL' : tekortUren > 0 ? 'TEKORT' : 'OK',
  }
}
